import React, { useEffect, useMemo, useState } from 'react';
import { Pressable, View } from 'react-native';
import { Image } from 'expo-image';
import { Link } from 'expo-router';

import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { MODEL_TOTAL } from '@/constants/modelInventory.generated';

import { Chip, useInView, useInteractive, useLanding } from './LandingKit';
import { modelBrand } from './landingModels';
import {
  ACCENT_BRIGHT,
  ACCENT_TINT,
  EASE,
  MONO,
  accentAlpha,
  isWeb,
  labelType,
  prefersReducedMotion,
} from './landingTokens';

/** Where the mock composer sends visitors. Signed-in users never see this page. */
const COMPOSER_HREF = '/signup';

/* Semantic colours inside the console. "Done" is the brand green; "live" is its lighter tint. */
const DONE = ACCENT_BRIGHT;
const LIVE = ACCENT_TINT;

/* ------------------------------------------------------------------ */
/* Content                                                             */
/* ------------------------------------------------------------------ */

/**
 * Example Work runs, one per deliverable type. Every step names a tool or
 * skill the agent actually has (see execution/orchestrator/agent-skills and
 * frontend/constants/work.js TOOL_LABELS), and every model is seeded.
 */
const SCENARIOS = [
  {
    label: 'Research',
    goal: 'Research our top five competitors and build a comparison deck with speaker notes.',
    steps: [
      { icon: 'globe', text: 'Searching the web', meta: '23 sources' },
      { icon: 'window-maximize', text: 'Reading pricing pages', host: 'crunchbase.com' },
      { icon: 'comments', text: 'Structuring the narrative', model: 'Claude' },
      { icon: 'image', text: 'Generating cover art', model: 'GPT Image' },
      { icon: 'file-powerpoint', text: 'Building eight slides', ms: 1500 },
      { icon: 'list-check', text: 'Checking every claim against sources' },
    ],
    deliverable: { name: 'competitor-landscape.pptx', meta: '8 slides · speaker notes', icon: 'file-powerpoint', color: '#D97706' },
  },
  {
    label: 'Spreadsheets',
    goal: 'Score these six vendors against our requirements and recommend one.',
    steps: [
      { icon: 'file-lines', text: 'Reading requirements.pdf' },
      { icon: 'globe', text: 'Pulling vendor documentation', meta: '6 vendors' },
      { icon: 'comments', text: 'Scoring against 12 criteria', model: 'GPT' },
      { icon: 'table', text: 'Building the workbook with live formulas', ms: 1500 },
      { icon: 'pen', text: 'Writing the recommendation tab', model: 'DeepSeek' },
    ],
    deliverable: { name: 'vendor-scorecard.xlsx', meta: '3 tabs · weighted scores', icon: 'file-excel', color: '#059669' },
  },
  {
    label: 'AI videos',
    goal: 'Make a cinematic launch film for the new app, with music and voiceover.',
    steps: [
      { icon: 'pen', text: 'Writing the script and shot list', model: 'Gemini' },
      { icon: 'image', text: 'Creating characters and keyframes for review', model: 'Seedream' },
      { icon: 'video', text: 'Animating the approved shots', model: 'Kling', ms: 1600 },
      { icon: 'music', text: 'Adding music and voiceover', model: 'MiniMax' },
      { icon: 'film', text: 'Assembling the final edit' },
    ],
    deliverable: { name: 'launch-film.mp4', meta: '1080p · music · voiceover', icon: 'file-video', color: '#7C3AED' },
  },
  {
    label: 'Documents',
    goal: 'Turn this forty-minute call recording into a client-ready brief.',
    steps: [
      { icon: 'waveform', text: 'Transcribing the call', meta: '40:12' },
      { icon: 'comments', text: 'Pulling out decisions and action items', model: 'Kimi' },
      { icon: 'pen', text: 'Drafting the brief' },
      { icon: 'chart-simple', text: 'Adding a timeline chart' },
      { icon: 'file-pdf', text: 'Laying out the PDF', ms: 1400 },
    ],
    deliverable: { name: 'client-brief.pdf', meta: '4 pages · sourced', icon: 'file-pdf', color: '#DC2626' },
  },
  {
    label: 'Websites & code',
    goal: 'Build a one-page site for the fundraiser and hand me the files.',
    steps: [
      { icon: 'window-maximize', text: 'Checking the venue details', host: 'eventbrite.com' },
      { icon: 'pen', text: 'Drafting the copy', model: 'DeepSeek' },
      { icon: 'image', text: 'Generating the hero image', model: 'Flux' },
      { icon: 'code', text: 'Coding the page', ms: 1500 },
      { icon: 'desktop', text: 'Testing it in the browser', host: 'localhost:3000' },
    ],
    deliverable: { name: 'fundraiser-site.zip', meta: 'HTML · CSS · images', icon: 'file-zipper', color: '#2563EB' },
  },
  {
    label: 'Video games',
    goal: 'Build a space arcade game with keyboard controls, scoring, and a restart button.',
    steps: [
      { icon: 'pen', text: 'Designing the rules and level', model: 'Claude' },
      { icon: 'image', text: 'Creating the game artwork', model: 'GPT Image' },
      { icon: 'code', text: 'Coding movement, collisions, and scoring', ms: 1500 },
      { icon: 'gamepad', text: 'Adding controls and a restart screen' },
      { icon: 'file-code', text: 'Packaging the playable HTML file' },
    ],
    deliverable: { name: 'space-arcade.html', meta: 'Play in chat · HTML source', icon: 'gamepad', color: '#0D9488' },
  },
];

const TYPE_MS = 24;
const STEP_MS = 1150;
const FIRST_STEP_DELAY_MS = 540;
const HOLD_MS = 3600;
const SWAP_MS = 420;

/* ------------------------------------------------------------------ */
/* Player                                                              */
/* ------------------------------------------------------------------ */

function settledState(index) {
  const scenario = SCENARIOS[index];
  return { index, typed: scenario.goal.length, step: scenario.steps.length, delivered: true, swapping: false };
}

/**
 * Drives one Work run after another: type the goal, run each step, deliver
 * the file, hold, then swap scenarios. Plain timers rather than Animated so
 * the trail can add and remove rows; each tick passes its own state forward,
 * so the loop never reads stale React state.
 */
function useRunPlayer(enabled, selection) {
  const [state, setState] = useState(() => (enabled
    ? { index: selection.index, typed: 0, step: -1, delivered: false, swapping: false }
    : settledState(selection.index)));

  useEffect(() => {
    if (!enabled) {
      setState(settledState(selection.index));
      return undefined;
    }
    const initial = { index: selection.index, typed: 0, step: -1, delivered: false, swapping: false };
    setState(initial);

    let timer = 0;
    const schedule = (fn, ms) => { timer = setTimeout(fn, ms); };

    const tick = (s) => {
      const scenario = SCENARIOS[s.index];
      let next;
      let wait;

      if (s.swapping) {
        next = { index: (s.index + 1) % SCENARIOS.length, typed: 0, step: -1, delivered: false, swapping: false };
        wait = 260;
      } else if (s.typed < scenario.goal.length) {
        next = { ...s, typed: s.typed + 1 };
        wait = TYPE_MS;
      } else if (s.step < 0) {
        next = { ...s, step: 0 };
        wait = FIRST_STEP_DELAY_MS;
      } else if (s.step < scenario.steps.length) {
        next = { ...s, step: s.step + 1 };
        wait = scenario.steps[s.step].ms || STEP_MS;
      } else if (!s.delivered) {
        next = { ...s, delivered: true };
        wait = HOLD_MS;
      } else {
        next = { ...s, swapping: true };
        wait = SWAP_MS;
      }

      setState(next);
      schedule(() => tick(next), wait);
    };

    schedule(() => tick(initial), 700);
    return () => clearTimeout(timer);
  }, [enabled, selection]);

  return state;
}

/* ------------------------------------------------------------------ */
/* Pieces                                                              */
/* ------------------------------------------------------------------ */

function StatusDot({ color = LIVE, live = true }) {
  return (
    <View style={{ width: 8, height: 8, alignItems: 'center', justifyContent: 'center' }}>
      {live && isWeb ? (
        <View
          dataSet={{ landingPulse: 'true' }}
          style={{ position: 'absolute', width: 8, height: 8, borderRadius: 4, backgroundColor: color, opacity: 0.5 }}
        />
      ) : null}
      <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: color }} />
    </View>
  );
}

/** Logo or monogram for a model, sized for a chip. */
function ModelMark({ name, size = 18 }) {
  const brand = modelBrand(name);
  const radius = Math.round(size / 3.2);
  if (brand.uri) {
    return (
      <View style={{ width: size, height: size, borderRadius: radius, overflow: 'hidden', backgroundColor: '#FFFFFF' }}>
        <Image source={{ uri: brand.uri }} style={{ width: size, height: size }} contentFit="cover" transition={150} />
      </View>
    );
  }
  return (
    <View
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        backgroundColor: brand.color,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Text weight="bold" style={{ color: '#FFFFFF', fontSize: Math.round(size * 0.52), lineHeight: size }}>
        {name.charAt(0)}
      </Text>
    </View>
  );
}

function ModelChip({ name, visible, delay = 0 }) {
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 7,
        paddingLeft: 5,
        paddingRight: 10,
        paddingVertical: 5,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: 'rgba(247,245,241,0.14)',
        backgroundColor: 'rgba(247,245,241,0.06)',
        ...(isWeb ? {
          opacity: visible ? 1 : 0,
          transform: [{ scale: visible ? 1 : 0.85 }, { translateY: visible ? 0 : 6 }],
          transition: `opacity 360ms ${EASE} ${delay}ms, transform 420ms ${EASE} ${delay}ms`,
        } : {}),
      }}
    >
      <ModelMark name={name} />
      <Text weight="bold" style={{ color: 'rgba(247,245,241,0.86)', fontSize: 11.5, letterSpacing: -0.1 }}>{name}</Text>
    </View>
  );
}

/**
 * The mock composer's shell. Renders as a real anchor on web so it is
 * crawlable and middle-clickable; hover state comes from `useInteractive`
 * because a function `style` does not survive `Link asChild`.
 */
function ComposerLink({ children, hovered, handlers, typing }) {
  const { c, isDark } = useLanding();
  const style = {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingLeft: 16,
    paddingRight: 8,
    paddingVertical: 8,
    minHeight: 54,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: hovered ? c.accent : typing ? c.accentRing : c.rule,
    backgroundColor: isDark ? 'rgba(243,246,243,0.03)' : '#FFFFFF',
    ...(isWeb ? {
      cursor: 'text',
      transition: `border-color 300ms ${EASE}, box-shadow 300ms ${EASE}`,
      boxShadow: hovered ? `0 0 0 4px ${c.accentWash}` : '0 0 0 0px transparent',
    } : {}),
  };

  const pressable = (
    <Pressable
      {...handlers}
      accessibilityRole="link"
      accessibilityLabel="Unlock Gab AI Work Agent with Plus"
      style={style}
    >
      {children}
    </Pressable>
  );

  if (!isWeb) return pressable;
  return (
    <Link href={COMPOSER_HREF} asChild>
      {pressable}
    </Link>
  );
}

/** One row of the activity trail. */
function StepRow({ step, status, index }) {
  const { c } = useLanding();
  const done = status === 'done';
  const active = status === 'active';
  const pending = status === 'pending';

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        paddingVertical: 9,
        ...(isWeb ? {
          opacity: pending ? 0.38 : 1,
          transform: [{ translateX: pending ? 4 : 0 }],
          transition: `opacity 420ms ${EASE}, transform 420ms ${EASE}`,
        } : {}),
      }}
    >
      <View style={{ width: 22, height: 22, alignItems: 'center', justifyContent: 'center' }}>
        <View
          style={{
            position: 'absolute',
            width: 20,
            height: 20,
            borderRadius: 10,
            borderWidth: 1.5,
            borderColor: done ? 'transparent' : c.ruleStrong,
            backgroundColor: done ? c.accentFill : 'transparent',
            ...(isWeb ? { transition: `background-color 300ms ${EASE}, border-color 300ms ${EASE}` } : {}),
          }}
        />
        {active && isWeb ? (
          <View
            dataSet={{ landingSpin: 'true' }}
            style={{
              position: 'absolute',
              width: 20,
              height: 20,
              borderRadius: 10,
              borderWidth: 1.5,
              borderColor: 'transparent',
              borderTopColor: c.accent,
              borderRightColor: c.accent,
            }}
          />
        ) : null}
        {done ? <Icon name="check" size={9} color="#FFFFFF" solid /> : (
          <Icon name={step.icon} size={9} color={active ? c.accent : c.faint} />
        )}
      </View>

      <Text
        weight={active ? 'semibold' : 'normal'}
        style={{ color: active ? c.ink : c.inkSoft, fontSize: 13.5, flex: 1, minWidth: 0, letterSpacing: -0.1 }}
        numberOfLines={1}
      >
        {step.text}
      </Text>

      {step.model ? (
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 6,
            paddingLeft: 4,
            paddingRight: 8,
            paddingVertical: 3,
            borderRadius: 999,
            borderWidth: 1,
            borderColor: c.rule,
            backgroundColor: c.panel,
          }}
        >
          <ModelMark name={step.model} size={14} />
          <Text weight="bold" style={{ color: c.muted, fontSize: 10.5 }}>{step.model}</Text>
        </View>
      ) : step.meta ? (
        <Text style={{ color: c.faint, fontSize: 11, fontVariant: ['tabular-nums'] }}>{step.meta}</Text>
      ) : step.host ? (
        <Text style={{ color: c.faint, fontSize: 11, fontFamily: MONO }} numberOfLines={1}>{step.host}</Text>
      ) : null}
    </View>
  );
}

/** The agent's screen: a browser it is driving, the models it has pulled in, and the file it hands back. */
function ComputerPanel({ scenario, state, compact }) {
  const startedSteps = scenario.steps.slice(0, Math.max(0, Math.min(state.step + 1, scenario.steps.length)));
  const models = [];
  startedSteps.forEach((step) => {
    if (step.model && !models.includes(step.model)) models.push(step.model);
  });
  const allModels = [];
  scenario.steps.forEach((step) => {
    if (step.model && !allModels.includes(step.model)) allModels.push(step.model);
  });

  const activeStep = state.step >= 0 && state.step < scenario.steps.length ? scenario.steps[state.step] : null;
  const lastHost = [...startedSteps].reverse().find((step) => step.host)?.host;
  const browsing = Boolean(activeStep?.host);
  const delivered = state.delivered;
  const { deliverable } = scenario;

  return (
    <View
      style={{
        flex: 1,
        minWidth: 0,
        backgroundColor: '#0D0C0B',
        padding: compact ? 16 : 20,
        gap: 14,
        justifyContent: 'space-between',
      }}
    >
      <View style={{ gap: 14 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <StatusDot live={!delivered} color={LIVE} />
            <Text weight="bold" style={{ color: 'rgba(247,245,241,0.62)', ...labelType(9.5, 0.18) }}>
              {delivered ? 'Ready for review' : 'Its own computer'}
            </Text>
          </View>
          <Text style={{ color: 'rgba(247,245,241,0.36)', fontSize: 10.5, fontFamily: MONO }}>sandbox · linux</Text>
        </View>

        {/* Browser window */}
        <View
          style={{
            borderRadius: 12,
            borderWidth: 1,
            borderColor: browsing ? accentAlpha(0.6, true) : 'rgba(247,245,241,0.12)',
            backgroundColor: 'rgba(247,245,241,0.04)',
            overflow: 'hidden',
            ...(isWeb ? { transition: `border-color 300ms ${EASE}` } : {}),
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              paddingHorizontal: 10,
              paddingVertical: 8,
              borderBottomWidth: 1,
              borderBottomColor: 'rgba(247,245,241,0.08)',
            }}
          >
            <View style={{ flexDirection: 'row', gap: 4 }}>
              {['#FF5F57', '#FEBC2E', '#28C840'].map((dot) => (
                <View key={dot} style={{ width: 7, height: 7, borderRadius: 4, backgroundColor: dot, opacity: 0.85 }} />
              ))}
            </View>
            <View
              style={{
                flex: 1,
                minWidth: 0,
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
                paddingHorizontal: 9,
                paddingVertical: 4,
                borderRadius: 6,
                backgroundColor: 'rgba(247,245,241,0.06)',
              }}
            >
              <Icon name="lock" size={8} color="rgba(247,245,241,0.4)" solid />
              <Text style={{ color: 'rgba(247,245,241,0.7)', fontSize: 10.5, fontFamily: MONO }} numberOfLines={1}>
                {lastHost || 'about:blank'}
              </Text>
            </View>
          </View>
          <View style={{ padding: 12, gap: 7, minHeight: compact ? 72 : 92 }}>
            {lastHost ? (
              <>
                <View style={{ width: '62%', height: 8, borderRadius: 4, backgroundColor: 'rgba(247,245,241,0.18)' }} />
                <View style={{ width: '88%', height: 6, borderRadius: 3, backgroundColor: 'rgba(247,245,241,0.10)' }} />
                <View style={{ width: '80%', height: 6, borderRadius: 3, backgroundColor: 'rgba(247,245,241,0.10)' }} />
                <View style={{ flexDirection: 'row', gap: 6, marginTop: 4 }}>
                  {[0, 1, 2].map((tile) => (
                    <View
                      key={tile}
                      style={{
                        flex: 1,
                        height: 22,
                        borderRadius: 5,
                        backgroundColor: browsing && tile === 1 ? accentAlpha(0.4, true) : 'rgba(247,245,241,0.08)',
                        ...(isWeb ? { transition: `background-color 300ms ${EASE}` } : {}),
                      }}
                    />
                  ))}
                </View>
              </>
            ) : (
              <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', paddingVertical: 10 }}>
                <Text style={{ color: 'rgba(247,245,241,0.3)', fontSize: 11 }}>
                  {delivered ? 'Browser closed' : 'Browser ready'}
                </Text>
              </View>
            )}
          </View>
        </View>

        {/* Models pulled into this run */}
        <View style={{ gap: 9 }}>
          <Text weight="bold" style={{ color: 'rgba(247,245,241,0.42)', ...labelType(9.5, 0.18) }}>
            Models on this run
          </Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, minHeight: 30 }}>
            {allModels.map((name, index) => (
              <ModelChip key={`${scenario.goal}-${name}`} name={name} visible={models.includes(name)} delay={index * 40} />
            ))}
          </View>
        </View>
      </View>

      {/* Deliverable */}
      <View
        style={{
          borderRadius: 14,
          borderWidth: 1,
          borderStyle: delivered ? 'solid' : 'dashed',
          borderColor: delivered ? 'rgba(247,245,241,0.18)' : 'rgba(247,245,241,0.14)',
          backgroundColor: delivered ? 'rgba(247,245,241,0.07)' : 'transparent',
          padding: 12,
          flexDirection: 'row',
          alignItems: 'center',
          gap: 12,
          ...(isWeb ? {
            transform: [{ translateY: delivered ? 0 : 6 }, { scale: delivered ? 1 : 0.985 }],
            transition: `all 460ms ${EASE}`,
          } : {}),
        }}
      >
        <View
          style={{
            width: 40,
            height: 40,
            borderRadius: 11,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: delivered ? `${deliverable.color}33` : 'rgba(247,245,241,0.05)',
            ...(isWeb ? { transition: `background-color 400ms ${EASE}` } : {}),
          }}
        >
          <Icon
            name={delivered ? deliverable.icon : 'file-export'}
            size={16}
            color={delivered ? deliverable.color : 'rgba(247,245,241,0.35)'}
            solid={delivered}
          />
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text
            weight="bold"
            numberOfLines={1}
            style={{ color: delivered ? '#F7F5F1' : 'rgba(247,245,241,0.4)', fontSize: 13, letterSpacing: -0.2 }}
          >
            {delivered ? deliverable.name : 'Deliverable'}
          </Text>
          <Text style={{ color: 'rgba(247,245,241,0.5)', fontSize: 11, marginTop: 2 }} numberOfLines={1}>
            {delivered ? deliverable.meta : 'Lands here when the work is done'}
          </Text>
        </View>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 6,
            paddingHorizontal: 11,
            paddingVertical: 7,
            borderRadius: 999,
            backgroundColor: delivered ? DONE : 'rgba(247,245,241,0.06)',
            ...(isWeb ? { opacity: delivered ? 1 : 0.4, transition: `all 400ms ${EASE}` } : {}),
          }}
        >
          <Icon name="download" size={10} color="#FFFFFF" />
          {!compact ? <Text weight="bold" style={{ color: '#FFFFFF', fontSize: 11 }}>Download</Text> : null}
        </View>
      </View>
    </View>
  );
}

/* ------------------------------------------------------------------ */

/**
 * The hero's product proof: a Work run playing out in real time. Left is the
 * activity trail the product actually shows; right is the agent's computer.
 * Scenarios rotate so the breadth of "anything" is demonstrated, not claimed.
 */
function HeroWorkShowcase() {
  const { c, isXS, isCompact } = useLanding();
  const [ref, inView] = useInView({ rootMargin: '0px 0px -5% 0px' });
  const reduced = prefersReducedMotion();
  const [selection, setSelection] = useState({ index: 2 });
  const state = useRunPlayer(isWeb && inView && !reduced, selection);
  const scenario = SCENARIOS[state.index];
  const { hovered: composerHovered, handlers: composerHandlers } = useInteractive();

  const typedGoal = scenario.goal.slice(0, state.typed);
  const typing = state.typed < scenario.goal.length;
  const running = !typing && state.step >= 0 && !state.delivered;
  const totalSteps = scenario.steps.length;
  const progress = state.delivered ? 1 : Math.max(0, Math.min(state.step, totalSteps)) / totalSteps;

  const statusText = useMemo(() => {
    if (state.swapping) return 'Starting the next task';
    if (typing) return 'Waiting for a goal';
    if (state.step < 0) return 'Booting up my computer';
    if (state.delivered) return 'Done · ready for review';
    return 'Working';
  }, [state.swapping, state.step, state.delivered, typing]);

  return (
    <View
      ref={ref}
      accessibilityLabel="Demonstration of a Gab AI Work Agent completing a task"
      style={{
        borderRadius: isXS ? 12 : 16,
        borderWidth: 1,
        borderColor: c.isDark ? 'rgba(247,245,241,0.14)' : 'rgba(20,17,14,0.14)',
        backgroundColor: c.cardSolid,
        overflow: 'hidden',
        ...(isWeb ? {
          boxShadow: c.shadowSoft,
          opacity: state.swapping ? 0.94 : 1,
          transition: `opacity ${SWAP_MS}ms ${EASE}`,
        } : {}),
      }}
    >
      <View style={{ paddingHorizontal: isXS ? 14 : 18, paddingTop: 18, paddingBottom: 14, borderBottomWidth: 1, borderBottomColor: c.rule, gap: 12 }}>
        <Text style={{ color: c.muted, ...labelType(10, 0.12) }}>Example workflows</Text>
        <View accessibilityRole="group" accessibilityLabel="Choose a Gab AI Work Agent example" style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
          {[2, 0, 3, 1, 4, 5].map((index) => (
            <Pressable
              key={SCENARIOS[index].label}
              accessibilityRole="button"
              accessibilityLabel={`Show ${SCENARIOS[index].label.toLowerCase()} example`}
              {...(isWeb ? { 'aria-pressed': state.index === index } : { accessibilityState: { selected: state.index === index } })}
              onPress={() => setSelection({ index })}
              style={{ paddingHorizontal: 12, minHeight: 40, justifyContent: 'center', borderRadius: 6, borderWidth: 1, borderColor: state.index === index ? c.accent : c.rule, backgroundColor: state.index === index ? c.accentWash : 'transparent', ...(isWeb ? { cursor: 'pointer' } : {}) }}
            >
              <Text weight={state.index === index ? 'bold' : 'medium'} style={{ color: state.index === index ? c.accent : c.inkSoft, fontSize: 12 }}>{SCENARIOS[index].label}</Text>
            </Pressable>
          ))}
        </View>
      </View>

      {/* Title bar */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 12,
          paddingHorizontal: isXS ? 14 : 18,
          paddingVertical: 12,
          borderBottomWidth: 1,
          borderBottomColor: c.rule,
          backgroundColor: c.panel,
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            padding: 3,
            borderRadius: 999,
            borderWidth: 1,
            borderColor: c.rule,
            backgroundColor: c.cardSolid,
          }}
        >
          {['Chat', 'Work'].map((mode) => {
            const on = mode === 'Work';
            return (
              <View
                key={mode}
                style={{
                  paddingHorizontal: 12,
                  paddingVertical: 5,
                  borderRadius: 999,
                  backgroundColor: on ? (c.isDark ? '#F7F5F1' : '#14110E') : 'transparent',
                }}
              >
                <Text weight="bold" style={{ color: on ? (c.isDark ? '#14110E' : '#F7F5F1') : c.muted, fontSize: 11.5 }}>
                  {mode === 'Work' ? 'Gab AI Work Agent' : mode}
                </Text>
              </View>
            );
          })}
        </View>

        <View style={{ flex: 1, minWidth: 0, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          {!isXS ? (
            <>
              <StatusDot live={running} color={state.delivered ? c.accent : running ? LIVE : c.faint} />
              <Text style={{ color: c.muted, fontSize: 12.5 }} numberOfLines={1}>{statusText}</Text>
            </>
          ) : null}
        </View>

        {!isCompact ? <Chip icon="bolt" tone="accent">{MODEL_TOTAL} models available</Chip> : null}
      </View>

      {/* Composer — a real click target: anyone who tries to type is sent to sign up. */}
      <View style={{ paddingHorizontal: isXS ? 14 : 18, paddingTop: 16, paddingBottom: 14, gap: 8 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <Text weight="bold" style={{ color: c.faint, ...labelType(9.5, 0.18) }}>Goal</Text>
          <Text style={{ color: c.faint, fontSize: 11 }}>
            {composerHovered ? 'Unlock Gab AI Work Agent with Plus' : 'Gab AI Work Agent is included with Plus'}
          </Text>
        </View>
        <ComposerLink hovered={composerHovered} handlers={composerHandlers} typing={typing}>
          <Icon name="paperclip" size={13} color={c.faint} />
          <View style={{ flex: 1, minWidth: 0, flexDirection: 'row', alignItems: 'center' }}>
            <Text style={{ color: c.ink, fontSize: isXS ? 14 : 15.5, lineHeight: isXS ? 20 : 22, letterSpacing: -0.15 }}>
              {typedGoal}
              {isWeb && typing ? (
                <Text dataSet={{ landingCaret: 'true' }} style={{ color: c.accent, fontSize: isXS ? 14 : 15.5, lineHeight: isXS ? 20 : 22 }}>|</Text>
              ) : null}
            </Text>
          </View>
          <View
            style={{
              width: 36,
              height: 36,
              borderRadius: 18,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: typing && !composerHovered ? c.panel : c.accentFill,
              ...(isWeb ? { transition: `background-color 300ms ${EASE}` } : {}),
            }}
          >
            <Icon name="arrow-up" size={13} color={typing && !composerHovered ? c.faint : '#FFFFFF'} solid />
          </View>
        </ComposerLink>
      </View>

      {/* Body */}
      <View style={{ flexDirection: isCompact ? 'column' : 'row', borderTopWidth: 1, borderTopColor: c.rule }}>
        <View style={{ flex: isCompact ? undefined : 1.15, minWidth: 0, paddingHorizontal: isXS ? 14 : 18, paddingVertical: 14 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <Text weight="bold" style={{ color: c.faint, ...labelType(9.5, 0.18) }}>Activity</Text>
            <Text style={{ color: c.faint, fontSize: 11, fontVariant: ['tabular-nums'] }}>
              {Math.min(Math.max(state.step, 0), totalSteps)}/{totalSteps} steps
            </Text>
          </View>

          <View style={{ minHeight: isCompact ? undefined : 6 * 40 }}>
            {scenario.steps.map((step, index) => {
              const status = index < state.step ? 'done' : index === state.step && !state.delivered ? 'active' : state.delivered ? 'done' : 'pending';
              return <StepRow key={`${state.index}-${index}`} step={step} status={status} index={index} />;
            })}
          </View>

          <View style={{ marginTop: 10, height: 4, borderRadius: 999, backgroundColor: c.rule, overflow: 'hidden' }}>
            <View
              style={{
                height: '100%',
                width: `${Math.round(progress * 100)}%`,
                borderRadius: 999,
                backgroundColor: state.delivered ? DONE : c.accentFill,
                overflow: 'hidden',
                ...(isWeb ? { transition: `width 600ms ${EASE}, background-color 400ms ${EASE}` } : {}),
              }}
            >
              {isWeb && running ? (
                <View
                  dataSet={{ landingShimmer: 'true' }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    width: '40%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)',
                  }}
                />
              ) : null}
            </View>
          </View>

          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 12 }}>
            <Icon name={state.delivered ? 'circle-check' : 'desktop'} size={11} color={state.delivered ? c.accent : c.faint} solid={state.delivered} />
            <Text style={{ color: c.faint, fontSize: 11.5 }} numberOfLines={1}>
              {state.delivered
                ? 'Checks the result before handing it over'
                : 'Plans, acts, and checks its own work'}
            </Text>
          </View>
        </View>

        <ComputerPanel scenario={scenario} state={state} compact={isCompact} />
      </View>
    </View>
  );
}

export default React.memo(HeroWorkShowcase);
